<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { getLabel } from '@/utils/data-display-utils';

import BaseSubscribe from '@/components/base/BaseSubscribe.vue';
import { constant } from '@/constant';
import { mailingListService } from '@/services/mailingList.service';
import { useUserStore } from '@/store/userStore';
import { showToaster } from '@/utils/toaster-util';
import { Locales } from '@/store/language.interface';

// STORE
const userStore = useUserStore();

// REFS
const userSubscribeRef = ref<InstanceType<typeof BaseSubscribe>>();
const userSubscribed = ref<boolean>(false);
const emailAlreadyInList = ref<boolean>(false);
const emailNeedsVerification = ref<boolean>(false);

// PROPS
const props = defineProps<{
  communityId: number;
  languageEnabled?: Locales[];
}>();

watch(
  () => userStore.user.isCommunityMailingListMember,
  (newValue) => (userSubscribed.value = newValue > 0),
);

// LIFECYCLE
onMounted(async () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const code = urlSearchParams.get('code');
  if (code && window.location.pathname === '/') {
    const successful = await mailingListService.verifyEmail(code);
    const translationKey = 'footer.email-verification';
    showToaster({
      message: getLabel(`${translationKey}.${successful ? 'successful' : 'unsuccessful'}`, props.languageEnabled),
      type: successful ? 'positive' : 'negative',
    });
  }
});

// FUNCTIONS
async function subscribe(values) {
  const subscriptionStatus = await mailingListService.subscribe(
    constant.MailingList.Parent.community,
    props.communityId,
    values.email,
  );
  resetForm();
  switch (subscriptionStatus) {
    case constant.MailingList.SubscriptionStatus.AlreadySubscribed:
      emailAlreadyInList.value = true;
      break;
    case constant.MailingList.SubscriptionStatus.NeedEmailVerification:
      emailNeedsVerification.value = true;
      break;
    case constant.MailingList.SubscriptionStatus.NewlySubscribed:
      userSubscribed.value = true;
      break;
  }
}

async function unsubscribe() {
  const isUnsubscribed = await mailingListService.unsubscribe(constant.MailingList.Parent.community, props.communityId);
  if (isUnsubscribed) {
    resetForm();
  }
}

function resetForm() {
  userSubscribed.value = false;
  emailAlreadyInList.value = false;
  emailNeedsVerification.value = false;
  userSubscribeRef.value?.resetForm();
}
</script>
<template>
  <div class="footer-subscribe">
    <div v-if="!userSubscribed" class="sub-title">
      {{ getLabel('footer.not-subscribed', props.languageEnabled) }}
    </div>
    <div v-else class="sub-title">
      {{ getLabel('footer.already-subscribed', props.languageEnabled) }}
    </div>
    <base-subscribe
      ref="userSubscribeRef"
      :user-subscribed="userSubscribed"
      :email-already-in-list="emailAlreadyInList"
      :email-needs-verification="emailNeedsVerification"
      :input-filled="true"
      :language-enabled="props.languageEnabled"
      @unsubscribe="unsubscribe"
      @subscribe="subscribe"
    />
  </div>
</template>
<style lang="scss">
.footer-subscribe {
  display: flex;
  flex-direction: column;
  gap: $space-md;

  .sub-title {
    font-weight: bold;
  }

  .user-subscribe {
    display: flex;
    flex-direction: column;
    gap: $space-sm;

    .buttons {
      display: flex;
      flex-direction: row;
      gap: $space-md;

      .subscribe-button {
        min-width: 120px;
      }

      .q-field {
        width: 60%;
      }

      .unsubscribe-button {
        background-color: $color-primary-darken;
      }
    }
  }
}
</style>
